const en = {
  common: {
    brand: "Consultants Sparks",
    tagline: "Projects. People. Growth.",
    menu: "Menu",
    close: "Close",
    loading: "Loading…",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    submit: "Submit",
    back: "Back",
    next: "Next",
    previous: "Previous",
    required: "Required",
    optional: "optional",
    yes: "Yes",
    no: "No",
    search: "Search",
    filters: "Filters",
    clear: "Reset",
    all: "All",
    none: "None",
    language: "Language",
    error: "Something went wrong. Please try again.",
  },
  nav: {
    home: "Home",
    about: "About",
    opportunities: "Opportunities",
    realEstate: "Real Estate",
    careers: "Careers",
    humanDev: "Human Development",
    projects: "Projects",
    contact: "Contact",
  },
  cta: {
    discover: "Discover opportunities",
    apply: "Submit my application",
    talk: "Talk with our team",
    exploreRealEstate: "Explore a career in real estate",
    login: "Sign in",
    register: "Create an account",
    portal: "My portal",
    admin: "Administration",
    logout: "Sign out",
  },
  home: {
    heroKicker: "Projects. People. Growth.",
    heroL1: "Develop the projects.",
    heroL2: "Reveal the potential.",
    heroL3: "Create the growth.",
    heroSub: "Consultants Sparks supports people, teams and organizations in developing new opportunities, projects and careers.",
    pillarsKicker: "Three pillars",
    pillars: {
      projects: { title: "Projects", body: "We help turn opportunities into structured, sustainable initiatives." },
      people: { title: "People", body: "We identify, develop and support people who are ready to grow." },
      growth: { title: "Growth", body: "We create connections between talent, organizations and opportunities." },
    },
    journeyKicker: "The journey",
    journeyTitle: "Structured support, from first contact to leadership",
    journeyLead: "Everyone moves at their own pace. Our role is to make each step clear and reachable.",
    journey: [
      { title: "Discover", body: "Understand where your potential can take you." },
      { title: "Evaluate", body: "A human conversation to map your strengths and goals." },
      { title: "Orient", body: "Identify the most coherent professional direction." },
      { title: "Train", body: "Build the skills required to succeed." },
      { title: "Deploy", body: "Start in a concrete role, with support." },
      { title: "Develop", body: "Progress, take responsibility, help others grow." },
    ],
    verticalsKicker: "Areas of activity",
    verticalsTitle: "Varied sectors, one standard",
    verticalsLead: "Our areas of activity evolve with the needs of the people and organizations we support.",
    orgKicker: "For organizations and projects",
    orgTitle: "Structure teams, activate opportunities",
    orgBody: "We work with organizations and project owners to identify talent, structure teams, develop markets and create lasting partnerships.",
    orgItems: [
      "Identify and attract the right people",
      "Structure and develop teams",
      "Develop new markets",
      "Create lasting partnerships",
    ],
    closingTitle: "Where can your potential take you?",
    closingBody: "You don't need to know exactly which path fits you. Start with a conversation.",
  },
  about: {
    kicker: "About",
    title: "An organization that develops projects and people",
    lead: "Consultants Sparks is a development organization: of projects, people, teams and growth.",
    p1: "We are not a staffing agency or a job board. Our work is to build lasting connections between people's ambitions and the real needs of organizations and projects.",
    p2: "Our approach is structured, human and verifiable. We move forward in clear stages, with decisions made by people, never by automation.",
    valuesKicker: "Our principles",
    values: [
      { title: "Rigour", body: "Clear, documented processes that respect people." },
      { title: "Confidentiality", body: "Business relationships and personal data remain protected." },
      { title: "Progression", body: "An organization grows when the people within it grow." },
    ],
    scaleKicker: "Our reach",
    scaleTitle: "Built to grow",
    scaleBody: "Our organizational architecture is designed to operate at local, provincial/state, national and international level.",
    scaleItems: ["Local", "Provincial / State", "National", "International"],
  },
  opportunities: {
    kicker: "Opportunities",
    title: "Explore the broad opportunity categories",
    lead: "These are the general categories in which Consultants Sparks develops people, teams and projects.",
    note: "These categories are indicative. No specific posting is published on this page; concrete opportunities are discussed directly with our team.",
    categoriesTitle: "Categories",
    cats: {
      real_estate_career: { title: "Real estate career", body: "Career, team and network development in the real estate sector." },
      insurance_career: { title: "Insurance and financial services career", body: "Entry paths, required qualifications and consultative development." },
      sales_representative: { title: "Sales representation", body: "Consultative selling, customer acquisition and territory development." },
      campaign_representative: { title: "Campaign representation", body: "Commercial campaigns, activations and charitable projects." },
      business_development: { title: "Business development", body: "Creating opportunities, partnerships and organizational growth." },
      automotive_sales: { title: "Automotive sector", body: "Commercial and business development opportunities in the automotive industry." },
      leadership_development: { title: "Leadership development", body: "Support for people who want to lead and grow teams." },
      project_opportunity: { title: "Project opportunity", body: "Participation in structured projects, in development or expansion." },
    },
  },
  realEstate: {
    kicker: "Real Estate",
    title: "Developing real estate talent, teams and projects",
    lead: "Consultants Sparks supports the development of talent, networks, teams and opportunities in the real estate sector.",
    p1: "Real estate is an important vertical of our activity: careers, network development, structuring sales teams and development projects.",
    p2: "We work with an international network of real estate professionals and specialized partners. Out of respect for confidentiality, no partner name is published without explicit approval.",
    areasKicker: "Areas of activity",
    areas: [
      { title: "Careers", body: "Supporting people who want to build a career in real estate." },
      { title: "Network development", body: "Growth and structuring of professional networks." },
      { title: "Sales teams", body: "Building and developing high-performing teams." },
      { title: "Development projects", body: "Contributing to residential and commercial projects." },
      { title: "Partnerships", body: "Creating lasting relationships with specialized partners." },
      { title: "International expansion", body: "Developing markets beyond borders." },
    ],
  },
  humanDev: {
    kicker: "Human Development",
    title: "An organization grows when the people within it grow",
    lead: "Human development is at the heart of Consultants Sparks.",
    p1: "We support people in discovering their potential, developing their skills and building a coherent professional path.",
    disclaimer: "We promise no income and guarantee no automatic advancement. Every path depends on the person's commitment, skills and context.",
    itemsKicker: "How we support people",
    items: [
      { title: "Support", body: "Human, structured follow-up that respects each person's pace." },
      { title: "Leadership", body: "Developing the ability to lead, decide and grow others." },
      { title: "Career", body: "Building a coherent path, step by step." },
      { title: "Training", body: "Acquiring the skills required for the target role." },
      { title: "Coaching", body: "Regular conversations to make concrete progress." },
      { title: "Progression", body: "Taking responsibility and contributing to larger projects." },
    ],
  },
  projects: {
    kicker: "Projects",
    title: "Turning opportunities into structured projects",
    lead: "Consultants Sparks develops projects: commercial, real estate, expansion, recruitment or partnership.",
    p1: "A project rarely starts with a complete plan. It starts with an opportunity, a team and a method.",
    typesKicker: "Project types",
    types: [
      { title: "Real estate", body: "Residential, commercial and development projects." },
      { title: "Commercial", body: "Market and business opportunity development." },
      { title: "Expansion", body: "Territorial and organizational growth." },
      { title: "Recruitment", body: "Structuring teams and human capacity." },
      { title: "Partnership", body: "Structured alliances between organizations." },
      { title: "Investment", body: "Projects requiring financial structuring." },
    ],
    processKicker: "Our method",
    process: [
      { title: "Exploration", body: "Understand the opportunity and its context." },
      { title: "Qualification", body: "Validate feasibility and conditions for success." },
      { title: "Structuring", body: "Define the team, stages and responsibilities." },
      { title: "Development", body: "Execute, measure and adjust." },
    ],
  },
  careers: {
    kicker: "Careers",
    title: "Find your next opportunity",
    lead: "Discover where your potential can take you. One general application is enough to start the conversation.",
    howKicker: "How it works",
    how: [
      { title: "Submit", body: "Complete the general application in a few minutes." },
      { title: "Connect", body: "A member of our team reaches out to you." },
      { title: "Orient", body: "Together we identify the most coherent direction." },
      { title: "Start", body: "You begin with structured support." },
    ],
    noFake: "We publish no fictitious postings. Concrete opportunities are presented directly during our conversations.",
  },
  contactAdmin: {
    title: "Contact messages",
    lead: "Messages received through the public form, with their VytaliLeads CRM sync state.",
    empty: "No message yet.",
    select: "Select a message.",
    syncLabel: "CRM sync",
    attempts: "attempt(s)",
    resend: "Resend to CRM",
    filterAll: "All",
    filterFailed: "To resend",
    filterUnread: "New",
    noUnread: "No new message.",
    source: "Source",
    sourceUnknown: "Unknown source",
    srcPage: "Page",
    srcLanding: "Landing page",
    srcLanguage: "Language",
    srcChannel: "Channel",
    srcReferrer: "Referrer",
    noFailed: "No message to resend.",
    resendOk: "Message resent to the CRM.",
    resendFailed: "Resend failed. The message is still stored.",
    notConfigured: "The VytaliLeads server-to-server connection is not configured. Messages are stored here and can be resent once the configuration is in place.",
  },
  contact: {
    kicker: "Contact",
    title: "Talk with our team",
    lead: "A question, a project, an application? Write to us.",
    name: "Full name",
    email: "Email",
    subject: "Subject",
    message: "Message",
    send: "Send message",
    success: "Thank you. Your message has been sent to our team.",
  },
  apply: {
    kicker: "General application",
    title: "Discover where your potential can take you",
    lead: "You don't need to know exactly which path fits you. Just answer, and we will help orient you.",
    steps: ["Your details", "Your experience", "Your interests", "Consents"],
    fields: {
      firstName: "First name",
      lastName: "Last name",
      email: "Email",
      phone: "Phone",
      city: "City",
      provinceState: "Province / State",
      country: "Country",
      language: "Preferred language",
      employmentStatus: "Current situation",
      currentRole: "Current role",
      yearsExperience: "Years of experience",
      education: "Education",
      availability: "Availability",
      transportation: "Transportation",
      experienceTitle: "Relevant experience",
      sales: "Sales experience",
      leadership: "Leadership experience",
      realEstate: "Real estate experience",
      insurance: "Insurance experience",
      automotive: "Automotive experience",
      entrepreneurship: "Entrepreneurial experience",
      mobilityTitle: "Mobility",
      travel: "Willing to travel",
      remote: "Interested in remote work",
      relocation: "Open to relocation",
      interests: "Areas of interest",
      careerGoals: "Career goals",
      motivation: "What motivates you",
      referralSource: "How did you hear about us?",
    },
    options: {
      employment: { employed: "Employed", self_employed: "Self-employed", student: "Student", between_jobs: "In transition", other: "Other" },
      years: { "0-1": "0 to 1 year", "2-3": "2 to 3 years", "3-5": "3 to 5 years", "5-10": "5 to 10 years", "10+": "More than 10 years" },
      education: { high_school: "High school", college: "College", university: "University", other: "Other" },
      availability: { immediate: "Immediate", "2_weeks": "In 2 weeks", "1_month": "In 1 month", flexible: "Flexible" },
      transportation: { car: "Car", public: "Public transit", none: "None" },
      referral: { web: "Web search", social: "Social media", referral: "Referred by someone", event: "Event", other: "Other" },
    },
    consent: {
      age: "I confirm that I am 18 years of age or older.",
      process: "I authorize Consultants Sparks to process my information for the evaluation of my application.",
      marketing: "I agree to receive communications about opportunities and activities at Consultants Sparks (optional).",
      version: "Consent version",
      separateNote: "Consent to process your application is separate from consent to marketing communications.",
    },
    ageError: "You must confirm that you are 18 years of age or older.",
    consentError: "Consent to process your application is required.",
    validation: {
      stepError: "Please complete the required fields of this step before continuing.",
      required: "This field is required.",
      email: "Please enter a valid email address.",
      phone: "Please enter a valid phone number (at least 6 characters).",
      country: "Please enter a valid country.",
    },
    success: {
      title: "Application received",
      body: "Thank you. Your application has been recorded and a member of our team will contact you.",
      account: "Create your account to follow the progress of your file.",
    },
  },
  auth: {
    loginTitle: "Sign in",
    loginLead: "Access your Consultants Sparks portal.",
    registerTitle: "Create an account",
    registerLead: "Create your account to follow your file.",
    email: "Email",
    password: "Password",
    firstName: "First name",
    lastName: "Last name",
    forgotLink: "Forgot your password?",
    noAccount: "No account yet?",
    hasAccount: "Already have an account?",
    forgotTitle: "Forgot password",
    forgotLead: "We will send you a reset link.",
    forgotSent: "If an account exists, a reset link has been sent.",
    resetTitle: "New password",
    resetLead: "Choose a new password.",
    resetDone: "Password updated. You can now sign in.",
    verifyTitle: "Email verification",
    verifying: "Verifying…",
    verified: "Your email address is verified.",
    verifyFailed: "This verification link is invalid or expired.",
    passwordRule: "Minimum 8 characters.",
    unverified: "Your email address is not verified yet.",
    resend: "Resend verification email",
    resent: "Verification email sent.",
  },
  portal: {
    title: "My portal",
    welcome: "Hello",
    statusTitle: "My application status",
    noApplication: "No application is linked to this account yet.",
    applyNow: "Submit my application",
    currentStage: "Current stage",
    historyTitle: "History",
    profileTitle: "My profile",
    accountTitle: "My account",
    nextStepTitle: "Next step",
    nextStep: {
      new: "Your application has been received. Our team is reviewing it.",
      to_contact: "Our team will contact you shortly.",
      contacted: "First contact has been made. Watch for our messages.",
      prequalification: "A prequalification is in progress.",
      interview_scheduled: "A meeting with our team is scheduled.",
      interview_completed: "Your meeting is complete. We will get back to you.",
      evaluation: "Your file is under evaluation.",
      orientation: "We are identifying the most coherent professional direction.",
      training: "A training stage is in progress.",
      ready_deployment: "Your file is ready for a start.",
      active: "You are active within our activities.",
      progressing: "You are progressing in your path.",
      not_retained: "Your file has not been retained at this time.",
      inactive: "Your file is currently inactive.",
    },
    updated: "Profile updated.",
    settingsLang: "Communication language",
  },
  admin: {
    dashboard: "Dashboard",
    candidates: "Candidates",
    pipeline: "Pipeline",
    users: "Users",
    audit: "Audit log",
    livechat: "Ask Vytali",
    contact: "Messages",
    recruitment: "Recruitment",
    settings: "Settings",
    kpi: { total: "Total candidates", new: "New candidates", active: "Active candidates", unassigned: "Unassigned" },
    byStage: "Candidates by stage",
    byVertical: "Candidates by interest",
    byGeo: "Candidates by territory",
    bySource: "Candidates by source",
    recent: "Recent applications",
    tasksDue: "Follow-ups due",
    noTasks: "No pending follow-up.",
    scopeAssigned: "Scope: your assigned candidates",
    scopeAll: "Scope: all candidates",
    table: { name: "Name", contact: "Contact", location: "Territory", stage: "Stage", recruiter: "Recruiter", created: "Created", interests: "Interests", actions: "Actions" },
    view: "View",
    assignTo: "Assign to",
    assignSuccess: "Assignment updated.",
    stageChanged: "Stage updated.",
    candidateNotFound: "Candidate not found or outside your scope.",
    downloadDenied: "Download not allowed for this candidate.",
    accessDenied: "Access denied: this section is restricted to administrators.",
    claim: "Take ownership",
    unassign: "Remove assignment",
    unassigned: "Unassigned",
    changeStage: "Change stage",
    stageReason: "Reason (optional)",
    notes: "Internal notes",
    addNote: "Add note",
    notePlaceholder: "Internal note…",
    sensitive: "Sensitive note",
    noNotes: "No notes.",
    tasks: "Follow-ups",
    addTask: "Add follow-up",
    taskTitle: "Follow-up title",
    dueDate: "Due date",
    markDone: "Mark as done",
    done: "Done",
    open: "Open",
    noTasksCandidate: "No follow-up.",
    history: "Pipeline history",
    profile: "Candidate profile",
    experience: "Experience",
    consent: "Consents",
    searchPlaceholder: "Name, email, phone…",
    filterStage: "Stage",
    filterVertical: "Interest",
    filterRecruiter: "Recruiter",
    filterCountry: "Country",
    resultsCount: "result(s)",
    noResults: "No candidate matches these criteria.",
    usersTitle: "Users and roles",
    role: "Role",
    status: "Status",
    activeLabel: "Active",
    inactiveLabel: "Inactive",
    lastSuperAdmin: "The last active super_admin is protected.",
    unassignedPool: "Unassigned pool",
    auditTitle: "Audit log",
    auditActor: "Actor",
    auditAction: "Action",
    auditEntity: "Entity",
    auditDate: "Date",
    settingsCompany: "Company",
    settingsLanguages: "Languages",
    settingsRecruitment: "Recruitment",
    settingsEmail: "Email",
    settingsPrivacy: "Privacy",
    settingsSecurity: "Security",
    settingsSaved: "Settings saved.",
    securityNote: "JWT authentication, bcrypt password hashing, login attempt limiting, RBAC enforced server-side.",
    emailNote: "Sender identity is configured through environment variables and cannot be edited here.",
    privacyNote: "Private organization names are never exposed publicly without explicit approval.",
  },
  stages: {
    new: "New",
    to_contact: "To contact",
    contacted: "Contacted",
    prequalification: "Prequalification",
    interview_scheduled: "Interview scheduled",
    interview_completed: "Interview completed",
    evaluation: "Evaluation",
    orientation: "Orientation",
    training: "Training",
    ready_deployment: "Ready for deployment",
    active: "Active",
    progressing: "Progressing",
    not_retained: "Not retained",
    inactive: "Inactive",
  },
  verticals: {
    REAL_ESTATE: "Real estate",
    INSURANCE_FINANCIAL_SERVICES: "Insurance and financial services",
    SALES_CAMPAIGNS: "Sales and commercial campaigns",
    CHARITY_CAMPAIGNS: "Charity campaigns",
    AUTOMOTIVE: "Automotive",
    BUSINESS_DEVELOPMENT: "Business development",
    PROJECTS_PARTNERSHIPS: "Projects and partnerships",
  },
  documents: {
    title: "My documents",
    rules: "Accepted formats",
    sizeUnit: "MB",
    type: "Document type",
    types: { resume: "Résumé / CV", certification: "Certification", other: "Other document" },
    upload: "Upload a document",
    uploaded: "Document uploaded.",
    deleted: "Document deleted.",
    failed: "Upload failed.",
    errCode: {
      INVALID_FILE_TYPE: "File type not accepted. Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG.",
      INVALID_MIME: "The file content does not match its extension. Re-save the document and try again.",
      FILE_TOO_LARGE: "File too large (maximum 8 MB). Compress the PDF and try again.",
      EMPTY_FILE: "The file is empty. Please select a valid document.",
      INVALID_DOCUMENT_TYPE: "Unsupported document type.",
      STORAGE_UNAVAILABLE: "Document storage is temporarily unavailable. Please try again shortly.",
      UNAUTHORIZED: "Your session has expired. Please sign in again and retry.",
      CANDIDATE_NOT_FOUND: "No application linked to this account: please submit your application first.",
      UPLOAD_FAILED: "Upload failed. Please try again shortly.",
    },
    errType: "Unsupported format. Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG.",
    errSize: "File too large. Maximum size: 8 MB.",
    errInvalid: "Invalid or empty file.",
    errAuth: "You must be signed in to upload a document.",
    empty: "No document yet.",
    download: "Download",
    delete: "Delete",
    staffTitle: "Candidate documents",
  },
  stats: {
    title: "My statistics",
    scope: "Scope: your personal activity",
    assigned: "Assigned candidates",
    new7: "New (7 days)",
    openTasks: "Open follow-ups",
    overdue: "Overdue follow-ups",
    activity: "Activity",
    moves7: "Stage changes (7 d)",
    moves30: "Stage changes (30 d)",
    notes30: "Notes (30 d)",
    claimed: "Candidates claimed",
    recentMoves: "Recent movements",
  },
  footer: {
    tagline: "Projects. People. Growth.",
    legal: "Legal",
    privacy: "Privacy Notice",
    terms: "Terms of Use",
    explore: "Explore",
    organization: "Organization",
    account: "Account",
    rights: "All rights reserved.",
    addressLabel: "Official address",
    address: "2828 Boul Laurier, Suite 700\nQuébec, QC G1V 0B9\nCanada",
    confidentiality: "Consultants Sparks protects the confidentiality of its business relationships, partners and candidates. Opportunities may vary based on profile, experience, qualifications and organizational needs.",
    navTitle: "Navigation",
    contactTitle: "Contact",
    callUs: "Call us",
    phone: "+1 418-549-6833",
    askTitle: "Ask Vytali",
    askLead: "A question about a career path, a project or a partnership? Ask it and our team will get back to you.",
    askCta: "Ask a question",
    askSoon: "Ask Vytali a question",
  },
  chat: {
    launcher: "Ask Vytali",
    open: "Open the Ask Vytali chat",
    callAria: "Call Consultants Sparks",
    close: "Close the Ask Vytali chat",
    subtitle: "Live assistance · Consultants Sparks",
    placeholder: "Type your question…",
    send: "Send",
    typing: "Ask Vytali is typing…",
    error: "The conversation is momentarily unavailable. You can reach us at +1 418-549-6833.",
    greeting: "Hello, I'm Ask Vytali from Consultants Sparks. Lovely to meet you. What brings you here today?",
    disclaimer: "Ask Vytali is a conversational assistant. This chat issues no decision and no guarantee of employment, income or interview.",
  },
};

export default en;
